import React from "react";

/**
 * Inline layout-panel icons for the Livestream channel, drawn to match the
 * minimalist neurobaker style (flat #B5B5B5 shapes on a 90×50 canvas, the same
 * geometry the CDN layout thumbnails use). They replace the outdated CDN icons
 * (which only showed text bars) with ones that reflect each layout's PHOTO
 * COUNT:
 *   1 → one photo (title + single photo)
 *   2 → one photo, alternative arrangement (photo + title, mirrored)
 *   3 → two photos   4 → three   5 → four   6 → five   7 → six
 *
 * Rendered inline (not via CDN) so the set can evolve with the layouts without
 * a CDN round-trip. The viewBox matches the 90×50 button so shapes stay crisp.
 */

const FILL = "#B5B5B5";
const VB_W = 90;
const VB_H = 50;

const Cell = ({x, y, w, h}: {x: number; y: number; w: number; h: number}) => (
  <rect x={x} y={y} width={w} height={h} rx={2.5} fill={FILL} />
);

const Bar = ({x, y, w}: {x: number; y: number; w: number}) => (
  <rect x={x} y={y} width={w} height={4} rx={2} fill={FILL} />
);

/**
 * Photo grid: `rows` lists the cell count per row. Cell size is derived so
 * every cell is identical across the whole grid and shorter rows are centered
 * — a clean, balanced look regardless of count.
 */
const Grid = ({rows}: {rows: number[]}) => {
  const padX = 8;
  const padY = 8;
  const gap = 4;
  const areaW = VB_W - padX * 2;
  const areaH = VB_H - padY * 2;
  const maxCols = Math.max(...rows);
  const cellW = (areaW - gap * (maxCols - 1)) / maxCols;
  const cellH = (areaH - gap * (rows.length - 1)) / rows.length;

  const cells: React.ReactElement[] = [];
  rows.forEach((cols, r) => {
    const rowW = cols * cellW + (cols - 1) * gap;
    const startX = padX + (areaW - rowW) / 2;
    const y = padY + r * (cellH + gap);
    for (let c = 0; c < cols; c++) {
      cells.push(
        <Cell key={`${r}-${c}`} x={startX + c * (cellW + gap)} y={y} w={cellW} h={cellH} />
      );
    }
  });
  return <>{cells}</>;
};

const CONTENT: Record<number, React.ReactNode> = {
  // one photo — title block + single photo on the right
  1: (
    <>
      <Bar x={8} y={17} w={30} />
      <Bar x={8} y={27} w={20} />
      <Cell x={52} y={12} w={30} h={26} />
    </>
  ),
  // one photo, alternative — portrait photo on the right, title on the left
  2: (
    <>
      <Bar x={8} y={17} w={30} />
      <Bar x={8} y={27} w={20} />
      <rect x={62} y={0} width={30} height={50} fill={FILL} />
    </>
  ),
  3: <Grid rows={[2]} />, // two photos
  4: <Grid rows={[3]} />, // three photos
  5: <Grid rows={[2, 2]} />, // four photos
  6: <Grid rows={[3, 2]} />, // five photos
  7: <Grid rows={[3, 3]} />, // six photos
};

export const hasLivestreamLayoutIcon = (id: number): boolean => id in CONTENT;

const LivestreamLayoutIcon = ({id}: {id: number}) => {
  const content = CONTENT[id];
  if (!content) return null;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {content}
    </svg>
  );
};

export default LivestreamLayoutIcon;
